// Generic country-page renderer driven by a single `data` object.
// Replaces the 700-line per-country React components going forward.
// Existing hand-built pages (Spain, Portugal, ... Croatia) are unaffected;
// new countries just author a data file in `src/data/country-pages/` and
// register one route line in App.js.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import TopBar from './TopBar';
import Footer from './Footer';
import EnquiryForm from './EnquiryForm';
import CountryCard from './CountryCard';
import FaqItem from './FaqItem';
import RevealProvider from './RevealProvider';
import { useCountryOverride } from '../hooks/useOverrides';
import { getCountry, COUNTRIES } from '../data/countries';

const DEFAULT_NATIONALITIES = [
  { code: 'us', label: '🇺🇸 United States' },
  { code: 'gb', label: '🇬🇧 United Kingdom' },
  { code: 'in', label: '🇮🇳 India' },
  { code: 'au', label: '🇦🇺 Australia' },
  { code: 'ca', label: '🇨🇦 Canada' },
  { code: 'br', label: '🇧🇷 Brazil' },
  { code: 'ph', label: '🇵🇭 Philippines' },
  { code: 'za', label: '🇿🇦 South Africa' },
  { code: 'ng', label: '🇳🇬 Nigeria' },
  { code: 'other', label: 'Other' },
];

// Safe HTML rendering helpers. Content in this component is authored by
// developers in /src/data/country-pages/*.js — not user input — but we still
// run every string through DOMPurify as defense-in-depth. Only a small
// inline-formatting tag whitelist is allowed.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['em', 'strong', 'br', 'span'],
  ALLOWED_ATTR: [],
};

function safeHtml(text) {
  return { __html: DOMPurify.sanitize(text ?? '', SANITIZE_CONFIG) };
}

function bold(text) {
  const html = (text ?? '').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  return { __html: DOMPurify.sanitize(html, SANITIZE_CONFIG) };
}

// Retained name for existing call sites; identical to safeHtml.
const emph = safeHtml;

export default function CountryPageRenderer({ data }) {
  const NATS = data.nationalities || DEFAULT_NATIONALITIES;
  const initialNat = NATS.find((n) => data.nationality.panels[n.code])?.code || NATS[0].code;
  const [natCode, setNatCode] = useState(initialNat);

  const country = getCountry(data.slug);
  const nat = data.nationality.panels[natCode] || data.nationality.panels[initialNat];

  // CMS overrides via shared context (single fetch shared across Home / Listing /
  // CountryCard / this renderer). Falls back to JS data-file URLs when no
  // override is set.
  const override = useCountryOverride(data.slug);
  const heroImage = override.hero || country?.image;
  const cityImage = (idx) => override.cities[idx] || data.place.cities[idx]?.image;

  // Merge CMS text overrides over the data file. v1 supports hero.dataCells
  // (label / value / note per cell). Empty/missing strings preserve the default.
  const cellOverrides = override.text?.dataCells || [];
  const mergedDataCells = data.hero.dataCells.map((cell, i) => {
    const o = cellOverrides[i] || {};
    return {
      label: o.label || cell.label,
      value: o.value || cell.value,
      note: o.note || cell.note,
    };
  });

  const elsewhere = (data.elsewhere?.countrySlugs || [])
    .map((s) => COUNTRIES.find((c) => c.slug === s))
    .filter(Boolean)
    .map((c) => ({ ...c, blurb: data.elsewhere.overrides?.[c.slug] || c.blurb }));

  const url = `https://nomadcompasshub.com/countries/${data.slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'GovernmentService',
    name: data.jsonLd.serviceName,
    serviceType: 'Digital Nomad Visa',
    provider: { '@type': 'GovernmentOrganization', name: data.jsonLd.providerName },
    areaServed: { '@type': 'Country', name: data.jsonLd.countryName || country?.name },
    url,
    termsOfService: country?.govSite,
    offers: { '@type': 'Offer', priceCurrency: data.jsonLd.priceCurrency || 'EUR', description: data.jsonLd.offerDescription },
  };
  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://nomadcompasshub.com/' },
      { '@type': 'ListItem', position: 2, name: 'Countries', item: 'https://nomadcompasshub.com/countries' },
      { '@type': 'ListItem', position: 3, name: data.meta.breadcrumb, item: url },
    ],
  };
  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };

  return (
    <RevealProvider>
      <Helmet>
        <title>{data.meta.title}</title>
        <meta name="description" content={data.meta.description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={data.meta.ogTitle || data.meta.title} />
        <meta property="og:description" content={data.meta.ogDescription || data.meta.description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
      </Helmet>

      <TopBar variant="page" breadcrumb={data.meta.breadcrumb} />

      <section className="cp-hero">
        <div className="cp-hero-top">
          <span className="cp-flag" aria-hidden="true">{data.flag}</span>
          {data.hero.eyebrow}
        </div>
        <h1 className="cp-hero-title" dangerouslySetInnerHTML={emph(data.hero.title)} />
        <p className="cp-hero-lead" dangerouslySetInnerHTML={emph(data.hero.lead)} />

        <div className="data-card reveal">
          {mergedDataCells.map((cell, i) => (
            <div className="data-cell" key={i}>
              <div className="data-label" dangerouslySetInnerHTML={emph(cell.label)} />
              <div className="data-val" dangerouslySetInnerHTML={emph(cell.value)} />
              <div className="data-note" dangerouslySetInnerHTML={emph(cell.note)} />
            </div>
          ))}
        </div>

        <div className="cp-hero-actions">
          <a href="#enquiry" className="btn-primary" data-testid="cta-apply">{data.hero.primaryCtaText}</a>
          <Link to={`/compare?a=${data.slug}`} className="country-compare-link" data-testid="cta-compare">Compare with another country →</Link>
          <div className="cp-jump">
            or jump to:{' '}
            {data.hero.jumps.map((j, i) => (
              <span key={i}>
                <a href={j.href}>{j.label}</a>
                {i < data.hero.jumps.length - 1 ? ' · ' : ''}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="moat-nat" id="nationality">
        <div className="moat-inner">
          <div className="reveal">
            <div className="moat-eyebrow">By your nationality <span className="pill">Interactive</span></div>
            <h2 className="moat-title" dangerouslySetInnerHTML={emph(data.nationality.title)} />
            <p className="moat-sub" dangerouslySetInnerHTML={emph(data.nationality.sub)} />
          </div>
          <div className="nat-picker reveal">
            {NATS.map((n) => (
              <button
                key={n.code}
                onClick={() => setNatCode(n.code)}
                className={`nat-chip ${natCode === n.code ? 'active' : ''}`}
                data-testid={`nat-${n.code}`}
              >
                {n.label}
              </button>
            ))}
          </div>
          <div className="nat-panel reveal" data-testid="nat-panel">
            <div className="nat-heading">{nat.heading}</div>
            <div className="nat-body">
              <p dangerouslySetInnerHTML={bold(nat.intro)} />
              <ul>{nat.bullets.map((b, i) => <li key={i} dangerouslySetInnerHTML={bold(b)} />)}</ul>
              <div className="nat-caveat">{nat.caveat}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="requirements">
        <div className="req-grid">
          <div className="req-col reveal">
            <h3>{data.requirements.qualifyTitle || 'Who qualifies'}</h3>
            <p>{data.requirements.qualifyIntro}</p>
            <div className="req-list">
              {data.requirements.qualifyItems.map((q, i) => (
                <div className="req-item" key={i}><div><strong>{q.strong}</strong><span>{q.span}</span></div></div>
              ))}
            </div>
          </div>
          <div className="req-col reveal">
            <h3>{data.requirements.docsTitle || 'Documents you’ll need'}</h3>
            <p>{data.requirements.docsIntro}</p>
            <div className="req-list">
              {data.requirements.docsItems.map((d, i) => (
                <div className="req-item" key={i}><div><strong>{d.strong}</strong><span>{d.span}</span></div></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="moat-cost" id="cost">
        <div className="cost-grid">
          <div className="cost-left reveal">
            <div className="moat-eyebrow">The real cost</div>
            <h2 className="moat-title" dangerouslySetInnerHTML={emph(data.cost.title)} />
            <p className="cost-intro" dangerouslySetInnerHTML={emph(data.cost.intro)} />
            <div className="cost-note">
              <strong>Why this transparency matters</strong>
              {data.cost.note}
            </div>
          </div>
          <div className="cost-table reveal">
            {data.cost.rows.map((r) => (
              <div className="cost-row" key={r.label}>
                <div className="cost-item"><strong>{r.label}</strong><span>{r.sub}</span></div>
                <div className="cost-amount">{r.eur}<small className="cost-amount-alt">{r.usd}</small></div>
              </div>
            ))}
            <div className="cost-total">
              <strong>{data.cost.totalLabel || 'Government & filing costs (single applicant, first year)'}</strong>
              <div className="cost-total-val">{data.cost.totalRange}</div>
              <div className="cost-total-sub">{data.cost.totalSub}</div>
            </div>
          </div>
        </div>
        {data.cost.bottomNote && (
          <div className="cost-bottom-note reveal" dangerouslySetInnerHTML={emph(data.cost.bottomNote)} />
        )}
      </section>

      <section className="moat-timeline" id="timeline">
        <div className="timeline-head reveal">
          <div className="moat-eyebrow">Week-by-week reality</div>
          <h2 className="moat-title" dangerouslySetInnerHTML={emph(data.timeline.title)} />
          <p className="moat-sub" dangerouslySetInnerHTML={emph(data.timeline.sub)} />
        </div>
        <div className="timeline">
          {data.timeline.rows.map((t, i) => (
            <div key={i} className="tline-row reveal">
              {i % 2 === 0 ? (
                <>
                  <div className={`tline-card ${t.milestone ? 'milestone' : ''}`}>
                    <div className="tline-title">{t.title}</div>
                    <div className="tline-body">{t.body}</div>
                    {t.caution && <div className="tline-caution">{t.caution}</div>}
                  </div>
                  <div className="tline-week">{t.week}</div>
                  <div className="tline-empty"></div>
                </>
              ) : (
                <>
                  <div className="tline-empty"></div>
                  <div className="tline-week">{t.week}</div>
                  <div className={`tline-card ${t.milestone ? 'milestone' : ''}`}>
                    <div className="tline-title">{t.title}</div>
                    <div className="tline-body">{t.body}</div>
                    {t.caution && <div className="tline-caution">{t.caution}</div>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="place-section">
        <div className="place-grid">
          <div className="place-image reveal">
            <img src={heroImage} alt={data.place.imageAlt || `${country?.name}`} />
          </div>
          <div className="place-text reveal">
            <div className="moat-eyebrow">Life in {country?.name}</div>
            <h2 dangerouslySetInnerHTML={emph(data.place.title)} />
            {data.place.paragraphs.map((p, i) => <p key={i} dangerouslySetInnerHTML={emph(p)} />)}
            <div className="place-stats">
              {data.place.stats.map((s, i) => (
                <div className="place-stat" key={i}><strong>{s.value}</strong><span>{s.label}</span></div>
              ))}
            </div>
          </div>
        </div>
        <div className="spain-cities reveal" aria-label={`Recommended ${country?.name} locations for nomads`}>
          {data.place.cities.map((c, i) => (
            <article className="spain-city" key={c.name}>
              <div className="spain-city-img">
                <img src={cityImage(i)} alt={`${c.name}, ${country?.name}`} loading="lazy" />
              </div>
              <div className="spain-city-body"><h3>{c.name}</h3><p>{c.blurb}</p></div>
            </article>
          ))}
        </div>
      </section>

      <section className="elsewhere">
        <div className="elsewhere-inner">
          <div className="elsewhere-head reveal">
            <h3>Considering <em>elsewhere?</em></h3>
            <Link to="/countries">View all 41 countries →</Link>
          </div>
          <p className="elsewhere-sub reveal" style={{ color: 'var(--warm-grey)', marginBottom: 22 }}>
            {data.elsewhere?.intro}
          </p>
          <div className="else-grid">{elsewhere.map((c) => <CountryCard key={c.slug} country={c} size="small" />)}</div>
        </div>
      </section>

      <section className="faq-section-cp">
        <div className="faq-wrap">
          <div className="reveal" style={{ textAlign: 'center' }}>
            <div className="moat-eyebrow">Frequently asked</div>
            <h2 className="moat-title">Questions before <em>you enquire.</em></h2>
          </div>
          {data.faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
        </div>
      </section>

      {Array.isArray(data.relatedPosts) && data.relatedPosts.length > 0 && (
        <section className="cp-related-reads" data-testid="related-reads">
          <div className="cp-related-inner">
            <div className="reveal">
              <div className="moat-eyebrow">Further reading</div>
              <h3>From the <em>Journal</em></h3>
            </div>
            <div className="cp-related-grid reveal">
              {data.relatedPosts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="cp-related-card" data-testid={`related-${p.slug}`}>
                  <div className="cp-related-pill">{p.category}</div>
                  <h4>{p.title}</h4>
                  <p>{p.excerpt}</p>
                  <span className="cp-related-readmore">Read the essay →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="enq" id="enquiry">
        <div className="enq-grid">
          <div className="enq-left reveal">
            <div className="moat-eyebrow">Ready to start?</div>
            <h2 dangerouslySetInnerHTML={emph(data.enquiry.title)} />
            {data.enquiry.paragraphs.map((p, i) => <p key={i} dangerouslySetInnerHTML={emph(p)} />)}
            <div className="enq-promises">
              {data.enquiry.promises.map((p, i) => (
                <div className="enq-promise" key={i}>{p}</div>
              ))}
            </div>
          </div>
          <EnquiryForm pageSource={data.slug} variant="country" defaultCountry={country?.name} />
        </div>
      </section>

      <Footer />
    </RevealProvider>
  );
}
